'use client';

import { ReactNode } from 'react';
import { SessionProvider, SessionProviderProps } from 'next-auth/react';

/**
 * Props for the AuthSessionProvider component
 */
export interface AuthSessionProviderProps extends SessionProviderProps {
  children: ReactNode;
}

/**
 * @description Provides the NextAuth session context to the application. Keep in mind that for `/pages` it will fetch the session
 * every time you switch between `/app` and `/pages`.
 *
 * The component has default values for:
 * - refetchOnWindowFocus: false
 * - refetchInterval: 0
 *
 * @example
 * // USAGE IN PAGES (_app.tsx)
 * // *****************************************************
 * import { AuthSessionProvider } from '@auth/context/AuthSessionProvider';
 *
 * const MyApp = ({ Component, pageProps: { session, ...pageProps } }: Props) => {
 *   ...
 *   <AuthSessionProvider session={session}>
 *      ...
 *  </AuthSessionProvider>
 *   ...
 * };
 *
 *
 * // USAGE IN APP (layout.tsx)
 * // *****************************************************
 * import { AuthSessionProvider } from '@auth/context/AuthSessionProvider';
 * import { auth } from 'auth';
 *
 * const RootLayout = async (props: RootLayoutProps) => {
 *   const authSession = await auth();
 *   ...
 *   <AuthSessionProvider session={authSession}>
 *      ...
 *   </AuthSessionProvider>
 *   ...
 * };
 */
export const AuthSessionProvider = ({
  children,
  refetchOnWindowFocus = false,
  refetchInterval = 0,
  ...props
}: AuthSessionProviderProps) => (
  <SessionProvider
    refetchOnWindowFocus={refetchOnWindowFocus}
    refetchInterval={refetchInterval}
    {...props}
  >
    {children}
  </SessionProvider>
);
